import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

export type SegmentedInputPattern = 'alphanumeric' | 'alpha' | 'numeric';

@Component({
  selector: 'app-segmented-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './segmented-input.component.html',
  styleUrl: './segmented-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SegmentedInputComponent),
      multi: true,
    },
  ],
})
export class SegmentedInputComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  /** Number of segments, e.g. 4 for [xxxx][xxxx][xxxx][xxxx] */
  @Input() segments = 4;
  /** Characters allowed per segment */
  @Input() segmentLength = 4;
  /** Visual separator rendered between segments */
  @Input() separator = '-';
  /** Character type restriction */
  @Input() pattern: SegmentedInputPattern = 'alphanumeric';
  /** Placeholder character shown inside each segment */
  @Input() placeholder = '';
  /** When set, visually groups a segment's digits with a space every N characters (e.g. 4 -> "0000 0000 ...") */
  @Input() groupSize?: number;
  /** Focuses the first segment on load so the user can type immediately */
  @Input() autofocus = false;
  /** Pulls focus back when the user clicks on empty page space, so typing always lands in the input */
  @Input() keepFocus = false;

  @ViewChildren('segInput') segInputs!: QueryList<ElementRef<HTMLInputElement>>;

  segmentValues: string[] = [];
  isDisabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    if (this.autofocus) {
      // Deferred so the focus lands after the view (and any route transition) has settled
      setTimeout(() => this.focusSegment(0));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['segments'] || changes['segmentLength']) {
      this.rebuildSegments();
    }
  }

  private rebuildSegments(): void {
    const current = this.segmentValues;
    this.segmentValues = Array.from({ length: this.segments }, (_, i) =>
      current[i] ?? ''
    );
  }

  // ControlValueAccessor

  /** Moves the cursor to the first segment, e.g. after the parent clears a wrong code */
  focusFirst(): void {
    this.focusSegment(0);
  }

  writeValue(value: string | null): void {
    const raw = (value ?? '').replace(new RegExp(`\\${this.separator}`, 'g'), '');
    this.segmentValues = Array.from({ length: this.segments }, (_, i) =>
      raw.slice(i * this.segmentLength, (i + 1) * this.segmentLength)
    );
    this.syncInputs();
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  // Event handlers

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const truncated = this.filter(input.value).slice(0, this.segmentLength);
    this.segmentValues[index] = truncated;

    if (this.groupSize) {
      const selectionStart = input.selectionStart ?? input.value.length;
      const digitsBeforeCursor = Math.min(
        this.filter(input.value.slice(0, selectionStart)).length,
        truncated.length
      );
      input.value = this.formatGroups(truncated, this.groupSize);
      const cursorPos = this.cursorPositionFor(truncated.length, digitsBeforeCursor, this.groupSize);
      input.setSelectionRange(cursorPos, cursorPos);
    } else {
      input.value = truncated;
    }

    this.emit();

    if (truncated.length === this.segmentLength && index < this.segments - 1) {
      this.focusSegment(index + 1);
    }
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && input.value === '' && index > 0) {
      event.preventDefault();
      this.focusSegment(index - 1, true);
    }

    if (event.key === 'ArrowRight' && input.selectionStart === input.value.length && index < this.segments - 1) {
      event.preventDefault();
      this.focusSegment(index + 1);
    }

    if (event.key === 'ArrowLeft' && input.selectionStart === 0 && index > 0) {
      event.preventDefault();
      this.focusSegment(index - 1, true);
    }

    if (!this.isAllowed(event.key)) {
      if (event.key.length === 1) {
        event.preventDefault();
      }
    }
  }

  onPaste(event: ClipboardEvent, startIndex: number): void {
    event.preventDefault();
    const pasted = this.filter(event.clipboardData?.getData('text') ?? '');
    let remaining = pasted;
    for (let i = startIndex; i < this.segments && remaining.length > 0; i++) {
      const chunk = remaining.slice(0, this.segmentLength);
      remaining = remaining.slice(this.segmentLength);
      this.segmentValues[i] = chunk;
    }
    this.syncInputs();
    this.emit();

    const lastFilled = Math.min(
      startIndex + Math.ceil(pasted.length / this.segmentLength),
      this.segments - 1
    );
    this.focusSegment(lastFilled);
  }

  onBlur(event: FocusEvent, index: number): void {
    this.onTouched();
    // relatedTarget is null only when focus went nowhere (click on blank space / non-focusable
    // element); moving to a button or another field is left alone so those stay usable.
    if (this.keepFocus && !this.isDisabled && !event.relatedTarget) {
      setTimeout(() => {
        if (!document.activeElement || document.activeElement === document.body) {
          this.focusSegment(index);
        }
      });
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  displayValue(val: string): string {
    return this.groupSize ? this.formatGroups(val, this.groupSize) : val;
  }

  placeholderFor(): string {
    const raw = this.placeholder.repeat(this.segmentLength);
    return this.groupSize ? this.formatGroups(raw, this.groupSize) : raw;
  }

  maxLengthFor(): number {
    if (!this.groupSize) return this.segmentLength;
    const spaces = Math.max(0, Math.ceil(this.segmentLength / this.groupSize) - 1);
    return this.segmentLength + spaces;
  }

  // Private helpers

  private filter(value: string): string {
    const upper = value.toUpperCase();
    switch (this.pattern) {
      case 'numeric':
        return upper.replace(/[^0-9]/g, '');
      case 'alpha':
        return upper.replace(/[^A-Z]/g, '');
      case 'alphanumeric':
      default:
        return upper.replace(/[^A-Z0-9]/g, '');
    }
  }

  private isAllowed(key: string): boolean {
    if (key.length !== 1) return true;
    return this.filter(key).length > 0;
  }

  private focusSegment(index: number, selectAll = false): void {
    const inputs = this.segInputs.toArray();
    const el = inputs[index]?.nativeElement;
    if (!el) return;
    el.focus();
    if (selectAll) {
      el.select();
    } else {
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }

  private syncInputs(): void {
    const inputs = this.segInputs?.toArray() ?? [];
    this.segmentValues.forEach((val, i) => {
      if (inputs[i]) {
        inputs[i].nativeElement.value = this.groupSize ? this.formatGroups(val, this.groupSize) : val;
      }
    });
  }

  private formatGroups(raw: string, groupSize: number): string {
    const groups: string[] = [];
    for (let i = 0; i < raw.length; i += groupSize) {
      groups.push(raw.slice(i, i + groupSize));
    }
    return groups.join(' ');
  }

  /** Maps a caret position expressed in raw digit count back to a position in the space-formatted string */
  private cursorPositionFor(rawLength: number, digitsBeforeCursor: number, groupSize: number): number {
    if (digitsBeforeCursor <= 0) return 0;
    const count = Math.min(digitsBeforeCursor, rawLength);
    const spaces = Math.floor((count - 1) / groupSize);
    return count + spaces;
  }

  private emit(): void {
    const combined = this.segmentValues.join(this.separator);
    this.onChange(combined);
  }
}
